import { Subscription } from '@demind-inc/core';
import dayjs from 'dayjs';

import { basicLifetimeOfferingId, DEFAULT_FREE_TRIAL_DAYS } from '../data-access';
import Stripe from 'stripe';
import { formatCurrencyPrice } from '../components';

/**
 * Retrieves the number of days in the free trial period from a given subscription.
 *
 * @param {Subscription} subscription - The subscription object containing free trial information.
 * @returns {number} The number of days in the free trial period. If the subscription does not have a free trial, returns the default number of free trial days.
 */
export const getFreeTrialPeriodDaysFromLSSubscription = (subscription: Subscription) => {
  if (!subscription.freeTrial || !subscription.freeTrial) {
    return DEFAULT_FREE_TRIAL_DAYS;
  }

  return Math.abs(
    dayjs(subscription.freeTrial.endDate).diff(dayjs(subscription.freeTrial.startDate), 'day')
  );
};

/**
 * Extracts metadata from a Stripe subscription.
 *
 * @param subscription - The Stripe subscription object containing the price information.
 * @returns An object containing the free trial days and offering ID from the subscription's product metadata.
 *
 * @remarks
 * - If the `freeTrialDays` metadata is not present, a default value (`DEFAULT_FREE_TRIAL_DAYS`) is used.
 * - The `offeringId` is directly taken from the product metadata.
 */
export const getMetaDataFromStripeSubscription = (subscription: Stripe.Price) => {
  const product = subscription.product as Stripe.Product;

  const freeTrialDays = product.metadata?.freeTrialDays;
  const offeringId = product.metadata?.offeringId;

  return {
    ...product.metadata,
    freeTrialDays: parseInt(freeTrialDays) || DEFAULT_FREE_TRIAL_DAYS,
    offeringId,
  };
};

export const isLifetimePlan = (offeringId: string) => {
  return offeringId === basicLifetimeOfferingId;
};

/**
 * Gets the free trial days from a plan's metadata.
 * Checks both plan.metadata and plan.product.metadata for freeTrialDays.
 *
 * @param plan - The Stripe Price object containing metadata.
 * @returns The number of free trial days, or DEFAULT_FREE_TRIAL_DAYS if not found.
 */
export const getFreeTrialDaysFromPlan = (plan: Stripe.Price): number => {
  // Check plan.metadata first
  const metadataTrialDays = plan.metadata?.freeTrialDays;
  if (metadataTrialDays) {
    const parsed = parseInt(metadataTrialDays);
    if (!isNaN(parsed)) {
      return parsed;
    }
  }

  // Check product.metadata as fallback
  const product = plan.product as Stripe.Product;
  const productTrialDays = product.metadata?.freeTrialDays;
  if (productTrialDays) {
    const parsed = parseInt(productTrialDays);
    if (!isNaN(parsed)) {
      return parsed;
    }
  }

  return DEFAULT_FREE_TRIAL_DAYS;
};

/**
 * Calculates the discount percentage for a yearly plan compared to weekly and monthly plans.
 * Returns the maximum discount percentage, rounded to the nearest multiple of 5.
 *
 * @param plans - Array of Stripe Price objects representing available subscription plans.
 * @param yearlyPlan - The yearly plan to calculate discount for.
 * @returns The discount percentage (rounded to nearest multiple of 5), or undefined if not applicable.
 */
export const calculateYearlyDiscountPercentage = (
  plans: Stripe.Price[],
  yearlyPlan: Stripe.Price
): number | undefined => {
  if (yearlyPlan.recurring?.interval !== 'year') {
    return undefined;
  }

  const weeklyPlan = plans.find((plan) => plan.recurring?.interval === 'week');
  const monthlyPlan = plans.find((plan) => plan.recurring?.interval === 'month');

  const discountPercentages: number[] = [];

  // Calculate discount compared to weekly plan (52 weeks in a year)
  if (weeklyPlan && weeklyPlan.currency === yearlyPlan.currency) {
    const weeklyPriceForYear = (weeklyPlan.unit_amount / 100) * 52;
    const yearlyPrice = yearlyPlan.unit_amount / 100;
    const discountAmount = weeklyPriceForYear - yearlyPrice;
    const discountPercentage = (discountAmount / weeklyPriceForYear) * 100;
    discountPercentages.push(discountPercentage);
  }

  // Calculate discount compared to monthly plan (12 months in a year)
  if (monthlyPlan && monthlyPlan.currency === yearlyPlan.currency) {
    const monthlyPriceForYear = (monthlyPlan.unit_amount / 100) * 12;
    const yearlyPrice = yearlyPlan.unit_amount / 100;
    const discountAmount = monthlyPriceForYear - yearlyPrice;
    const discountPercentage = (discountAmount / monthlyPriceForYear) * 100;
    discountPercentages.push(discountPercentage);
  }

  if (discountPercentages.length === 0) {
    return undefined;
  }

  // Return the maximum discount (best deal), rounded to the nearest multiple of 5
  const maxDiscount = Math.max(...discountPercentages);
  return Math.round(maxDiscount / 5) * 5;
};

/**
 * Calculates the original price from a discounted price and discount percentage.
 * Returns a formatted currency string.
 *
 * @param plan - The Stripe Price object containing the discounted price.
 * @param discountPercentage - The discount percentage (e.g., 50 for 50% off).
 * @returns A formatted currency string of the original price, or undefined if discountPercentage is invalid.
 *
 * @example
 * // If plan price is $5.00 and discount is 50%, original price is $10.00
 * calculateOriginalPriceFromDiscount(plan, 50) // Returns "$10.00"
 */
export const calculateOriginalPriceFromDiscount = (
  plan: Stripe.Price,
  discountPercentage: number | undefined
): string | undefined => {
  if (!discountPercentage || discountPercentage <= 0 || discountPercentage >= 100) {
    return undefined;
  }

  const discountedPrice = plan.unit_amount / 100; // Convert from cents to dollars
  const originalPrice = discountedPrice / (1 - discountPercentage / 100);
  const currency = plan.currency;

  return formatCurrencyPrice(currency, originalPrice);
};

export const sortPlans = (plans: Stripe.Price[]) => {
  return plans.sort((a, b) => {
    const aIsLifetime = a.lookup_key?.includes('basic_lifetime');
    const bIsLifetime = b.lookup_key?.includes('basic_lifetime');
    const aRecurring = a.recurring?.interval;
    const bRecurring = b.recurring?.interval;

    if (aIsLifetime && !bIsLifetime) return 1;
    if (!aIsLifetime && bIsLifetime) return -1;
    if (aIsLifetime && bIsLifetime) return 0;

    if (aRecurring === 'month' && bRecurring !== 'month') return -1;
    if (aRecurring !== 'month' && bRecurring === 'month') return 1;

    if (aRecurring === 'week' && bRecurring !== 'week') return -1;
    if (aRecurring !== 'week' && bRecurring === 'week') return 1;

    if (aRecurring === 'year' && bRecurring !== 'year') return -1;
    if (aRecurring !== 'year' && bRecurring === 'year') return 1;
  });
};
